import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { ResponsiveImageFragment } from "../../types.graphql"
import { cn } from "@utils/tw"
import { useMemo } from "react"

export type FlowgramImageType = {
  svgContent: string | null
} & ResponsiveImageFragment

export const FlowgramImage = ({
  flowgramImage,
  className,
  strokeHeavy = false,
}: {
  flowgramImage: FlowgramImageType
  className?: string
  strokeHeavy?: boolean
}) => {
  return (
    <div className={cn("aspect-square type-title-s relative", className)} data-transition="fade-up">
      {flowgramImage.svgContent ? (
        <FlowgramSvgFileDisplay svg={flowgramImage.svgContent} strokeHeavy={strokeHeavy} />
      ) : (
        <ResponsiveImage {...flowgramImage} max objectFit="cover" />
      )}
    </div>
  )
}

function FlowgramSvgFileDisplay({ svg, strokeHeavy }: { svg: string; strokeHeavy: boolean }) {
  const display = useMemo(() => {
    try {
      let svgWidth = parseFloat(svg.match(/width="([\d.]+)(\w*)"/)?.[1] ?? "0")
      let svgHeight = parseFloat(svg.match(/height="([\d.]+)(\w*)"/)?.[1] ?? "0")

      if (svgWidth === 0 || svgHeight === 0) {
        // try to get it from viewBox
        const viewBoxMatch = svg.match(/viewBox="([\d.\s]+)"/)
        if (viewBoxMatch) {
          const viewBoxValues = viewBoxMatch[1].split(" ").map(parseFloat)
          if (viewBoxValues.length === 4) {
            const [minX, minY, width, height] = viewBoxValues
            svgWidth = width
            svgHeight = height
          }
        }
      }

      const PADDING_AMOUNT = 0.025
      const padding = svgWidth * PADDING_AMOUNT
      svg = svg.replace(/<\?xml [^>]+\?>/, "") // Remove XML declaration
      if (svgWidth > 0 && svgHeight > 0) {
        svg = svg.replace(
          /<svg [^>]+>/,
          `<svg width="100%" height="100%" viewBox="${-padding} ${-padding} ${svgWidth + padding * 2} ${svgHeight + padding * 2}">`,
        )
      }

      return (
        <div
          className={cn(
            "absolute inset-0 [&_svg]:size-full [&_path]:!stroke-current",
            strokeHeavy ? "[&_path]:!stroke-[15px]" : "[&_path]:!stroke-[10px]",
          )}
          style={{ margin: `${-PADDING_AMOUNT * 100}%` } as React.CSSProperties}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      )
    } catch (err) {
      console.error(err)
    }
    return null
  }, [svg])

  return <>{display}</>
}
